import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LeftArrow } from '../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from '../../assets/icons/right-arrow.svg';
import getCurrentTimeInLocalISO from '../../shared-functions/CurrentTimeInLocalISO';

interface TimelineDataProperties {
  start: string;
  end: string;
  status: string;
  sizeInPX?: number;
  defaultEndDateUsed?: boolean;
}

interface TimelineLabelProperties {
  name: string;
  color: string;
  key: string;
}

interface CustomTimelineChartProperties {
  title?: string;
  timelineData: TimelineDataProperties[];
  timelineLabel: TimelineLabelProperties[];
  hourSizeInPX?: number;
  date: string; // Date in YYYY-MM-DD -> Used to merge same status and fill empty data in timeline data
  defaultStatusToShow?: string;
  dataLoading: boolean;
  showLegend?: boolean;
}

/**
 * Function to set a endsAtAdjusted date for the missing data
 * @param {any} sessions timeline sessions array
 * @param {any} dayEnd day end string
 * @returns {any} processed timeline sessions
 */
function processTimelineData(
  sessions: TimelineDataProperties[],
  dayEnd: string,
): TimelineDataProperties[] {
  // Sort the session data based on createdAt (earliest first)
  const createdAtSorted = [...sessions].sort((a, b) => {
    return new Date(a.start).getTime() - new Date(b.start).getTime();
  });

  return createdAtSorted.map((session, index) => {
    // If endsAtAdjusted is null, we need to calculate it
    if (session.end === null) {
      let newEndsAt: string;

      // If this isn't the first session, use the previous session's startsAtAdjusted
      if (index === sessions.length - 1) {
        // Add current local time
        newEndsAt = getCurrentTimeInLocalISO();
      } else {
        newEndsAt = sessions[index + 1].start;
      }

      // Cap at 23:59:00Z if it exceeds the day end
      if (new Date(newEndsAt) > new Date(dayEnd)) {
        newEndsAt = dayEnd;
      }

      // Return a new object with the calculated endsAtAdjusted
      return {
        ...session,
        end: newEndsAt,
        defaultEndDateUsed: true, // Just to identify
      };
    }

    // If endsAtAdjusted is not null, return the session as-is
    return session;
  });
}

/**
 * Processes timeline data by merging overlapping 'online' periods,
 * filling gaps as 'hide-data' periods, and calculating sizeInPX for each period.
 * Ensures coverage from 00:00 to 23:59 of the specified date.
 * @param {any} data - Array of timeline data entries.
 * @param {any} hourPX - Number of pixels representing one hour.
 * @param {any} date - The date for which the timeline is processed (format: 'YYYY-MM-DD').
 * @param {any} defaultStatusToShow - The defaultStatusToShow
 * @returns {any} Array of processed timeline data entries.
 */
function processTimelineDataProperties(
  data: TimelineDataProperties[],
  hourPX: number,
  date: string,
  defaultStatusToShow?: string,
): TimelineDataProperties[] {
  const minutesPerPX = 60 / hourPX;
  const dayStart = new Date(`${date}T00:00:00Z`);
  const dayEnd = new Date(`${date}T23:59:00Z`);

  // If no data, return a full day of hided-data
  if (data.length === 0) {
    return [
      {
        start: dayStart.toISOString(),
        end: dayEnd.toISOString(),
        status: defaultStatusToShow || `hided-data-${date}`,
        sizeInPX: Math.round((dayEnd.getTime() - dayStart.getTime()) / (60000 * minutesPerPX)),
      },
    ];
  }

  // Convert string timestamps to Date objects for processing
  const withDates = data.map((period) => ({
    ...period,
    startDate: new Date(period.start),
    endDate: new Date(period.end),
  }));

  // Filter and clip to our date range
  const normalizedData = withDates
    .filter((period) => period.endDate > dayStart && period.startDate < dayEnd)
    .map((period) => ({
      ...period,
      startDate: period.startDate < dayStart ? dayStart : period.startDate,
      endDate: period.endDate > dayEnd ? dayEnd : period.endDate,
    }));

  // Group by status
  const statusGroups: Record<string, typeof normalizedData> = {};
  normalizedData.forEach((period) => {
    if (!statusGroups[period.status]) {
      statusGroups[period.status] = [];
    }
    statusGroups[period.status].push(period);
  });

  // Merge each status group
  const mergedGroups: TimelineDataProperties[][] = Object.values(statusGroups).map((group) => {
    if (group.length === 0) return [];

    // Sort by start time
    const sorted = [...group].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    const merged: TimelineDataProperties[] = [];

    /* eslint-disable no-restricted-syntax */
    for (const period of sorted) {
      const last = merged[merged.length - 1];
      if (last && period.startDate <= new Date(last.end)) {
        // Merge overlapping periods
        const lastEnd = new Date(last.end);
        last.end = new Date(Math.max(lastEnd.getTime(), period.endDate.getTime())).toISOString();
      } else {
        merged.push({
          start: period.startDate.toISOString(),
          end: period.endDate.toISOString(),
          status: period.status,
        });
      }
    }

    return merged;
  });

  // Combine all merged groups and sort by start time
  const allMergedPeriods = mergedGroups
    .flat()
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  // Fill gaps with hided-data periods
  const fullTimeline: TimelineDataProperties[] = [];
  let currentTime = dayStart;

  /* eslint-disable no-restricted-syntax */
  for (const period of allMergedPeriods) {
    const periodStart = new Date(period.start);
    if (periodStart > currentTime) {
      // Add hided-data period
      fullTimeline.push({
        start: currentTime.toISOString(),
        end: periodStart.toISOString(),
        status: defaultStatusToShow || `hided-data-${date}`,
        sizeInPX: Math.round(
          (periodStart.getTime() - currentTime.getTime()) / (60000 * minutesPerPX),
        ),
      });
    }

    // Add the current period
    const periodEnd = new Date(period.end);
    fullTimeline.push({
      ...period,
      sizeInPX: Math.round((periodEnd.getTime() - periodStart.getTime()) / (60000 * minutesPerPX)),
    });

    currentTime = periodEnd;
  }

  // Add final hided-data period if needed
  if (currentTime < dayEnd) {
    fullTimeline.push({
      start: currentTime.toISOString(),
      end: dayEnd.toISOString(),
      status: defaultStatusToShow || `hided-data-${date}`,
      sizeInPX: Math.round((dayEnd.getTime() - currentTime.getTime()) / (60000 * minutesPerPX)),
    });
  }

  return fullTimeline;
}

/**
 * Convert index to time
 * @param {any} index index number
 * @returns {any} time string
 */
function indexToTime(index: number) {
  const hour = index % 24;
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;

  return `${displayHour}:00 ${period}`;
}

/**
 * Format ISO time to local time HH:MM AM/PM
 * @param {any} isoString ISO date string
 * @returns {any} formatted time string
 */
function formatToLocalTime(isoString: string): string {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours %= 12;
  hours = hours || 12; // the hour '0' should be '12'
  return `${hours}:${minutes} ${ampm}`;
}

/**
 * Format start and end date/time range to duration string
 * @param {any} start start ISO date string
 * @param {any} end end ISO date string
 * @returns {any} duration string
 */
function getDurationString(start: string, end: string): string {
  const diffMs = new Date(end).getTime() - new Date(start).getTime();
  if (diffMs <= 0) return '0 min';
  const totalMinutes = Math.round(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [];
  if (hours > 0) parts.push(hours === 1 ? `${hours} hr` : `${hours} hrs`);
  if (minutes > 0) parts.push(minutes === 1 ? `${minutes} min` : `${minutes} mins`);
  return parts.length > 0 ? parts.join(' ') : '0 min';
}

/**
 * Get translated or capitalized status name
 * @param {any} status status key
 * @param {any} timelineLabel labels config
 * @param {any} t translate function
 * @returns {any} status display name
 */
function getStatusName(status: string, timelineLabel: TimelineLabelProperties[], t: any): string {
  if (!status) return '';
  if (status.startsWith('hided-data-')) {
    return t('noData', { defaultValue: 'No Data' });
  }
  const foundLabel = timelineLabel.find((label) => label.key === status.toLowerCase());
  if (foundLabel) return foundLabel.name;
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Component to display a timeline graph for a single day
 * @param {any} properties properties
 * @returns {any} custom timeline chart
 */
function CustomTimelineChart(properties: CustomTimelineChartProperties) {
  const {
    timelineData,
    timelineLabel,
    title,
    hourSizeInPX,
    date,
    defaultStatusToShow,
    dataLoading = false,
    showLegend = true,
  } = properties;
  const { t, i18n } = useTranslation('devices');
  const [timelineDataToShow, setTimelineDataToShow] = useState<TimelineDataProperties[]>([]);

  const initialHourSize = hourSizeInPX && hourSizeInPX > 60 ? hourSizeInPX : 60; // If it less than, scss may affect, and there will time label will hide
  const [currentHourSize, setCurrentHourSize] = useState(initialHourSize);

  const wrapperReference = useRef<HTMLDivElement | null>(null);
  const scrollReference = useRef<HTMLDivElement | null>(null);
  const timeViewReference = useRef<HTMLDivElement | null>(null);
  const zoomAnchorReference = useRef<number | null>(null);
  const scrollAmount = 2 * currentHourSize; // scroll by 2 hours as step

  const [hoveredSegment, setHoveredSegment] = useState<TimelineDataProperties | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  /**
   * Zoom the timeline chart in
   */
  const handleZoomIn = () => {
    zoomAnchorReference.current = null;
    setCurrentHourSize((previous) => Math.min(previous + 20, 240));
  };

  /**
   * Zoom the timeline chart out
   */
  const handleZoomOut = () => {
    zoomAnchorReference.current = null;
    setCurrentHourSize((previous) => Math.max(previous - 20, 60));
  };

  /**
   * Handle mouse move over a timeline segment to update the tooltip coordinates
   * @param {any} eventObject react mouse event
   */
  const handleMouseMove = (eventObject: React.MouseEvent) => {
    if (wrapperReference.current) {
      const rect = wrapperReference.current.getBoundingClientRect();
      const x = eventObject.clientX - rect.left;
      const y = eventObject.clientY - rect.top;
      setTooltipPosition({ x, y });
    }
  };

  /**
   * Synchronize scroll between main chart and time axis labels
   */
  const handleChartScroll = () => {
    if (scrollReference.current && timeViewReference.current) {
      timeViewReference.current.scrollLeft = scrollReference.current.scrollLeft;
    }
  };

  /**
   * Scroll functionality left
   */
  const scrollLeft = () => {
    if (scrollReference.current && timeViewReference.current) {
      scrollReference.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      timeViewReference.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  /**
   * Scroll functionality right
   */
  const scrollRight = () => {
    if (scrollReference.current && timeViewReference.current) {
      scrollReference.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      timeViewReference.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  /**
   * Function to find the color of the bar from legends
   * @param {any} status status string
   * @returns {any} background color string
   */
  const getBackgroundColor = (status: string): string => {
    let bgColor = 'transparent';

    const foundLabel = timelineLabel.find((label) => label.key === status?.toLowerCase());

    if (foundLabel) {
      bgColor = foundLabel?.color;
    }

    return bgColor;
  };

  const previousHourSizeReference = useRef(currentHourSize);

  useEffect(() => {
    const processedTimeLineData = processTimelineData(timelineData, `${date}T23:59:00Z`);
    setTimelineDataToShow(
      processTimelineDataProperties(
        processedTimeLineData,
        currentHourSize,
        date,
        defaultStatusToShow,
      ),
    );
  }, [timelineData, currentHourSize, date, defaultStatusToShow]);

  // Reset scroll positions only when timeline data or date changes
  useEffect(() => {
    if (scrollReference.current && timeViewReference.current) {
      scrollReference.current.scrollTo({ left: 0, behavior: 'auto' });
      timeViewReference.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [timelineData, date]);

  // Adjust scroll positions proportionally to keep the view centered when zooming
  useEffect(() => {
    if (scrollReference.current) {
      const ratio = currentHourSize / previousHourSizeReference.current;
      if (ratio !== 1) {
        // Use the anchor if set, otherwise default to the center of the scroll viewport
        const anchor =
          zoomAnchorReference.current !== null
            ? zoomAnchorReference.current
            : scrollReference.current.clientWidth / 2;

        const newScrollLeft = (scrollReference.current.scrollLeft + anchor) * ratio - anchor;

        scrollReference.current.scrollLeft = newScrollLeft;
        if (timeViewReference.current) {
          timeViewReference.current.scrollLeft = newScrollLeft;
        }
      }
    }
    previousHourSizeReference.current = currentHourSize;
    zoomAnchorReference.current = null; // reset anchor after zoom is applied
  }, [currentHourSize]);

  // Set up wheel event listener for trackpad/mouse zoom & horizontal scroll
  useEffect(() => {
    const scrollElement = scrollReference.current;
    if (!scrollElement) {
      return () => {};
    }

    /**
     * Handle trackpad zoom, mouse zoom, and scroll behaviors
     * @param {any} event wheel event
     */
    const handleWheel = (event: WheelEvent) => {
      // event.ctrlKey is true for pinch-to-zoom on trackpad and Ctrl + mouse wheel scroll
      if (event.ctrlKey) {
        event.preventDefault();

        // Calculate mouse position relative to the scroll container's viewport
        const rect = scrollElement.getBoundingClientRect();
        zoomAnchorReference.current = event.clientX - rect.left;

        const zoomDelta = -event.deltaY * 0.2;
        setCurrentHourSize((previous) => {
          return Math.max(60, Math.min(240, previous + zoomDelta));
        });
      } else if (event.deltaY !== 0) {
        // Convert vertical scroll wheel event to horizontal scroll
        const canScrollLeft = scrollElement.scrollLeft > 0;
        const canScrollRight =
          scrollElement.scrollLeft < scrollElement.scrollWidth - scrollElement.clientWidth;

        // Only prevent default and scroll horizontally if we have scrollable horizontal overflow
        if ((event.deltaY > 0 && canScrollRight) || (event.deltaY < 0 && canScrollLeft)) {
          event.preventDefault();
          scrollElement.scrollLeft += event.deltaY;
          if (timeViewReference.current) {
            timeViewReference.current.scrollLeft = scrollElement.scrollLeft;
          }
        }
      }
    };

    scrollElement.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      scrollElement.removeEventListener('wheel', handleWheel);
    };
  }, [dataLoading]);

  return (
    <div className="custom-timeline-chart-wrap" ref={wrapperReference}>
      <div className="custom-timeline-chart-header">
        <h3>{title}</h3>
        <div className="custom-timeline-chart-controls">
          <div className="zoom-controls">
            <button
              type="button"
              className="zoom-btn"
              onClick={handleZoomOut}
              disabled={currentHourSize <= 60}
              title={t('zoomOut', { defaultValue: 'Zoom Out' })}>
              -
            </button>
            <span className="zoom-value">
              {Math.round((currentHourSize / initialHourSize) * 100)}%
            </span>
            <button
              type="button"
              className="zoom-btn"
              onClick={handleZoomIn}
              disabled={currentHourSize >= 240}
              title={t('zoomIn', { defaultValue: 'Zoom In' })}>
              +
            </button>
          </div>
          {showLegend && (
            <div className="custom-timeline-chart-legend">
              {timelineLabel?.map((eachLabel, index) => (
                <div key={index} className="custom-timeline-chart-legend-item">
                  <div
                    className="custom-timeline-chart-legend-tag"
                    style={{ backgroundColor: eachLabel.color }}
                  />
                  <span style={{ color: eachLabel.color }}>{eachLabel.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="custom-timeline-chart-container">
        <div
          className="arrow-container left"
          role="presentation"
          onClick={scrollLeft}
          style={{
            transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
          <LeftArrow />
        </div>
        {dataLoading ? (
          <div className="custom-timeline-chart-loading">
            <div className="skeleton" />
          </div>
        ) : (
          <div
            ref={scrollReference}
            className="custom-timeline-chart"
            onScroll={handleChartScroll}
            style={
              {
                '--columnLength': timelineDataToShow.length,
              } as React.CSSProperties
            }>
            {timelineDataToShow
              ?.filter((eachData) => eachData?.sizeInPX !== 0)
              ?.map((eachData, index) => (
                <div
                  key={index}
                  className="time-line-bar"
                  style={
                    {
                      width: `${eachData?.sizeInPX}px`,
                      // transition: 'all 0.4s ease-out', // Animation not working smoothly as expected
                      backgroundColor: getBackgroundColor(eachData?.status),
                    } as React.CSSProperties
                  }
                  onMouseEnter={() => setHoveredSegment(eachData)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => setHoveredSegment(null)}
                />
              ))}
          </div>
        )}
        <div
          className="arrow-container"
          role="presentation"
          onClick={scrollRight}
          style={{
            transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
          <RightArrow />
        </div>
      </div>
      <div className="custom-timeline-time-view" ref={timeViewReference}>
        <div
          className="custom-timeline-time-view-container"
          style={{
            width: `${24 * currentHourSize}px`,
          }}>
          {Array.from({ length: 24 }, (_, index) => (
            <div
              className="custom-timeline-chart-time-label"
              key={index}
              style={{
                left: `${index * currentHourSize}px`,
              }}>
              {indexToTime(index)}
            </div>
          ))}
        </div>
      </div>
      {hoveredSegment && (
        <div
          className="custom-timeline-tooltip"
          style={{
            position: 'absolute',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, 12px)',
            pointerEvents: 'none',
            zIndex: 9999,
          }}>
          <div
            className="tooltip-title"
            style={{ borderLeftColor: getBackgroundColor(hoveredSegment.status) }}>
            {getStatusName(hoveredSegment.status, timelineLabel, t)}
          </div>
          <div className="tooltip-content">
            <div className="tooltip-time">
              {formatToLocalTime(hoveredSegment.start)} - {formatToLocalTime(hoveredSegment.end)}
            </div>
            <div className="tooltip-duration">
              {t('duration', { defaultValue: 'Duration' })}:{' '}
              {getDurationString(hoveredSegment.start, hoveredSegment.end)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomTimelineChart;
